import { supabase } from "@/lib/supabase";

export const STAFF_LOCATION_ACCESS_TYPE = "staff" as const;
export const ASSIGNMENT_PLACEHOLDER_SECTION_NAME = "Strefa ogólna";

export type LocationAccessType = "permanent" | "staff";

export interface LocationAccessRow {
  id: string;
  location_id: string;
  user_id: string;
  access_type: string;
  expires_at: string | null;
  created_at: string | null;
  unit_number: string | null;
}

export function isAssignmentPlaceholderSection(name: string | null | undefined): boolean {
  return (name ?? "").trim().toLowerCase() === ASSIGNMENT_PLACEHOLDER_SECTION_NAME.toLowerCase();
}

export function isActiveStaffLocationAccess(row: {
  expires_at?: string | null;
  unit_number?: string | null;
}): boolean {
  if (row.unit_number != null && String(row.unit_number).trim() !== "") return false;
  if (!row.expires_at) return true;
  return new Date(row.expires_at).getTime() > Date.now();
}

export async function fetchStaffLocationAccessIds(staffUserId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from("location_access")
    .select("location_id, expires_at, unit_number")
    .eq("user_id", staffUserId);

  if (error) {
    console.error("[fetchStaffLocationAccessIds]", error);
    throw error;
  }

  return [
    ...new Set(
      (data ?? [])
        .filter((row) => isActiveStaffLocationAccess(row))
        .map((row) => row.location_id)
        .filter((id): id is string => Boolean(id))
    ),
  ];
}

export async function grantStaffLocationAccess(
  locationId: string,
  staffUserId: string
): Promise<{ alreadyAssigned: boolean }> {
  const { error } = await supabase.from("location_access").insert({
    location_id: locationId,
    user_id: staffUserId,
    access_type: STAFF_LOCATION_ACCESS_TYPE,
  });

  if (error) {
    if (error.code === "23505") {
      return { alreadyAssigned: true };
    }
    console.error("[grantStaffLocationAccess]", error);
    throw error;
  }

  return { alreadyAssigned: false };
}

export async function revokeStaffLocationAccess(
  locationId: string,
  staffUserId: string
): Promise<void> {
  const { error } = await supabase
    .from("location_access")
    .delete()
    .eq("location_id", locationId)
    .eq("user_id", staffUserId)
    .is("unit_number", null);

  if (error) {
    console.error("[revokeStaffLocationAccess]", error);
    throw error;
  }
}

export async function isStaffAssignedToLocationViaAccess(
  staffUserId: string,
  locationId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from("location_access")
    .select("id, expires_at, unit_number")
    .eq("user_id", staffUserId)
    .eq("location_id", locationId)
    .is("unit_number", null)
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error("[isStaffAssignedToLocationViaAccess]", error);
    throw error;
  }

  if (!data) return false;
  return isActiveStaffLocationAccess(data);
}
